import "./App.css";
import CssBaseline from "@mui/material/CssBaseline";
import { TanStackDevtools } from "@tanstack/react-devtools";
import Home from "./Home";
import React from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { ThemeProvider } from "@mui/material/styles";
import { createAxiosClient } from "@ntg/component-library/utilities";
import {
    createCSDarkTheme,
    createCSTheme,
} from "@ntg/component-library/theming";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { useQuery } from "@tanstack/react-query";
import {
    LayoutContextProvider,
    UserContextProvider,
} from "@ntg/component-library/contexts";
import { DesktopLayout } from "@ntg/component-library/app";
import { AnimatePresence } from "motion/react";
import { getDrawerContents } from "./DrawerContents";
import ProcessMonitorPage from "./monitor/ProcessMonitorPage";
import SalesOrderSearchContainer from "./sales/SalesOrderSearchContainer";
import ServiceOrderSearchContainer from "./service/ServiceOrderSearchContainer";
import ManufacturingContainer from "./manufacturing/ManufacturingSearchContainer";
import ManufacturingDetailContainer from "./manufacturing/ManufacturingDetailContainer";
import SalesOrderContainer from "./sales/SalesOrderContainer";
import ServiceOrderDetailContainer from "./service/ServiceOrderDetailContainer";
import DataExplorerPage from "./explorer/DataExplorerPage";
import ODataExplorerPage from "./odataexplorer/ODataExplorerPage";
import { useProcessMonitorStore } from "./stores/useProcessMonitorStore";
import { formatHex, oklch, filterBrightness, Oklch } from "culori";

/**
 * Ensures a minimum lightness for a given OKLCH color.
 * If the color is too dark, it adjusts only the lightness component.
 *
 * @param color The input color in OKLCH
 * @param minL Minimum lightness (0 to 1)
 * @returns Adjusted color with enforced lightness
 */
function ensureMinimumLightness(color: Oklch, minL: number): Oklch {
    return {
        ...color,
        l: Math.max(color.l, minL),
    };
}
export default function App() {
    const axiosAuth = createAxiosClient("Auth");

    const { isLoading, error } = useQuery({
        queryFn: async () => {
            const response = await axiosAuth.get("/Auth/validate");
            return response.data;
        },
        queryKey: ["validate"],
    });

    if (isLoading) return "Authenticating...";

    if (error) return "An error has occurred: " + error.message;

    const isDarkMode: boolean = false;
    const menuBar = "#164063ff";

    const base = oklch(menuBar);
    if (!base) throw new Error("Invalid base color");

    // shift base color by:  l= lightness (0-1), c=chroma (0-0.4), h=hue (angle 0-360)
    const shift = (lShift: number, cShift: number, hShift: number) => ({
        mode: "oklch" as const,
        l: Math.max(0, Math.min(1, base.l + lShift)),
        c: Math.max(0, base.c + cShift),
        h: ((base.h ?? 0) + hShift + 360) % 360,
    });

    const primaryTitle = formatHex(shift(isDarkMode ? -0.05 : 0.2, -0.03, 5));

    const secondaryHeader = formatHex(
        shift(isDarkMode ? -0.05 : 0.1, -0.04, 5),
    );

    const rawPrimary = shift(0.1, -0.01, 0); // saturated version of menuBar
    const rawSecondary = shift(0.1, -0.02, -90); // hue-shifted variant
    const rawTertiary = shift(0.1, -0.03, 90); // hue-shifted variant
    const rawQuaternary = shift(0.1, -0.04, 180); // hue-shifted variant

    const primary = formatHex(ensureMinimumLightness(rawPrimary, 0.55)); // enforce brightness
    const secondary = formatHex(ensureMinimumLightness(rawSecondary, 0.55));
    const tertiary = formatHex(ensureMinimumLightness(rawTertiary, 0.55));
    const quaternary = formatHex(ensureMinimumLightness(rawQuaternary, 0.55));

    const indicator = formatHex(shift(0.2, 0.05, -90));

    let brighten = filterBrightness(isDarkMode ? 0.0 : 0.95, "oklch");

    const background = formatHex(shift(isDarkMode ? -0.3 : 0.9, -0.09, 0));
    const backgroundPaper = formatHex(brighten(background));

    let theme;

    if (isDarkMode) {
        theme = createCSDarkTheme({
            background: background,
            backgroundPaper: backgroundPaper ?? "#000",
            menuBar: menuBar,
            secondaryMenuBar: secondaryHeader,
            indicator: indicator,
            primary: primary,
            primaryTitle: primaryTitle,
            secondaryHeader: secondaryHeader,
            secondary: secondary,
            tertiary: tertiary,
            quaternary: quaternary,
        });
    } else {
        theme = createCSTheme({
            background: background,
            backgroundPaper: backgroundPaper ?? "#fff",
            menuBar: menuBar,
            secondaryMenuBar: secondaryHeader,
            indicator: indicator,
            primary: primary,
            primaryTitle: primaryTitle,
            secondaryHeader: secondaryHeader,
            secondary: secondary,
            tertiary: tertiary,
            quaternary: quaternary,
        });
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TanStackDevtools
                plugins={[
                    {
                        name: "TanStack Query",
                        render: <ReactQueryDevtoolsPanel />,
                        defaultOpen: true,
                    },
                ]}
            />
            <UserContextProvider>
                <BrowserRouter>
                    <AppLayout />
                </BrowserRouter>
            </UserContextProvider>
        </ThemeProvider>
    );
}

function AppLayout() {
    const activeCount = useProcessMonitorStore((state) => state.activeCount);

    return (
        <LayoutContextProvider
            DrawerContents={getDrawerContents(activeCount)}
            ModuleName={"FinanceAndOperations"}
            ModuleTitle={"D365 F&O"}
        >
            <DesktopLayout
                MainContent={
                    <AnimatePresence mode="wait">
                        <Routes>
                            <Route element={<Home />} path="/" />
                            <Route element={<Home />} path="/home" />
                            <Route
                                element={<SalesOrderSearchContainer />}
                                path="/salesorder"
                            />
                            <Route
                                path="/SalesOrderHeader/:id"
                                element={<SalesOrderContainer />}
                            >
                                <Route
                                    path="customer"
                                    element={null}
                                />
                                <Route
                                    path="header"
                                    element={null}
                                />
                                <Route
                                    path="vatnum"
                                    element={null}
                                />
                                <Route
                                    path="headercharge"
                                    element={null}
                                />
                                <Route
                                    path="commit/:instanceId"
                                    element={null}
                                />
                            </Route>
                            <Route
                                element={
                                    <div>
                                        <ServiceOrderSearchContainer />
                                    </div>
                                }
                                path="/serviceorder"
                            />
                            <Route
                                path="/ServiceOrderHeader/:id"
                                element={<ServiceOrderDetailContainer />}
                            >
                                <Route
                                    path="commit/:instanceId"
                                    element={null}
                                />
                            </Route>
                            <Route
                                element={<ManufacturingContainer />}
                                path="/manufacturing"
                            />
                            <Route
                                path="/ManufacturingItem/:id"
                                element={<ManufacturingDetailContainer />}
                            >
                                <Route
                                    path="commit/:instanceId"
                                    element={null}
                                />
                            </Route>
                            <Route
                                element={<ODataExplorerPage />}
                                path="/odata-explorer"
                            />
                            <Route
                                element={<DataExplorerPage />}
                                path="/explorer/:entryType/:id"
                            />
                            <Route
                                path="/monitor"
                                element={<ProcessMonitorPage />}
                            >
                                <Route
                                    path=":instanceId"
                                    element={null}
                                />
                            </Route>
                        </Routes>
                    </AnimatePresence>
                }
                TerminalContent={<div></div>}
            />
        </LayoutContextProvider>
    );
}
